/* eslint-disable @typescript-eslint/no-floating-promises */

import { Component } from 'react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { throwErrorAsync } from '../throwError/throwError';
import { callIfPossible } from '../functions/functions';
import { Metrics } from '../Metrics';
import { addPreload } from './internal/loadableRegistry';
import { jsx as _jsx } from "react/jsx-runtime";
const MAX_RETRY_ATTEMPTS = 3;

/**
 * The options object passed to a Loadable HOC.
 */

/**
 * The type for the loader function passed to Loadable. Returns a promise,
 * typically a dynamic `import()`.
 *
 * @example
 * ```typescript
 * function createLoadable(loader: LoadableLoader) {
 *   return Loadable({
 *     loader,
 *   });
 * }
 * const A = createLoadable(
 *   () => import('./CoolComponent')
 * );
 * ```
 */

/**
 * The props on Loadable's `LoadingComponent`.
 */

function makeLoadable(loader, name) {
  let promise = null;
  let remainingRetries = MAX_RETRY_ATTEMPTS;
  const state = {
    loading: false,
    result: null,
    error: null
  };
  function init() {
    if (promise == null) {
      state.loading = true;
      state.result = null;
      state.error = null;
      if (loader != null) {
        var _loaderPromise;
        let loaderPromise = loader();
        if (typeof ((_loaderPromise = loaderPromise) === null || _loaderPromise === void 0 ? void 0 : _loaderPromise.then) !== 'function') {
          loaderPromise = Promise.resolve(loaderPromise);
        }
        promise = loaderPromise.then(result => {
          // If this is a retry attempt that succeeded, log to metrics-js with the number of retries it took
          if (remainingRetries < MAX_RETRY_ATTEMPTS) {
            Metrics.counter('loadable-chunk-retry-succeeded', Object.assign({
              retryAttempt: Number(MAX_RETRY_ATTEMPTS - remainingRetries).toString()
            }, name != null && {
              name
            })).increment();
          }
          state.result = result;
          return result;
        }).catch(err => {
          if (remainingRetries) {
            remainingRetries--;
            return retry(true);
          }
          state.error = err;
          throw err;
        }).finally(() => {
          state.loading = false;
          remainingRetries = MAX_RETRY_ATTEMPTS;
        });
      }
    }
    return promise;
  }
  function retry(shouldForceRetry = false) {
    if (!state.loading || shouldForceRetry) {
      promise = null;
    }
    return init();
  }
  function getState() {
    return state;
  }
  function subscribe(obs) {
    let unsubscribed = false;
    const unsubscribe = () => {
      unsubscribed = true;
    };
    init().then(() => {
      if (unsubscribed) return;
      callIfPossible(obs.next, state.result);
    }).catch(() => {
      if (unsubscribed) return;
      callIfPossible(obs.error, state.error);
    }).finally(() => {
      if (unsubscribed) return;
      callIfPossible(obs.complete);
    });
    return unsubscribe;
  }
  return {
    init,
    retry,
    subscribe,
    getState
  };
}
function resolveImport(obj) {
  if (obj && (obj.__esModule || 'default' in obj)) {
    return obj.default;
  }
  return obj;
}
function renderResolved(result, props) {
  const ResolvedComponent = resolveImport(result);
  return /*#__PURE__*/_jsx(ResolvedComponent, Object.assign({}, props));
}
function RenderSpinner({
  pastDelay
}) {
  if (pastDelay) return /*#__PURE__*/_jsx(LoadingSpinner, {
    showResult: false
  });
  return null;
}
function RenderNothing() {
  return null;
}

// Updates state so the next render can show the ErrorComponent
function getDerivedStateFromError(error) {
  return {
    didCatchError: true,
    thrownError: error
  };
}
export function Loadable(options) {
  const {
    name,
    loader: untypedLoader = null,
    typedLoader = null,
    LoadingComponent = RenderSpinner,
    ErrorComponent = RenderNothing,
    delay = 200,
    render = renderResolved,
    spinnerMinRenderTime = 50
  } = options;
  if (LoadingComponent == null) {
    throw new Error('Loadable requires a `LoadingComponent`');
  }
  if (untypedLoader != null && typedLoader != null) {
    throw new Error('Loadable accepts either `loader` or `typedLoader`, but not both. Use `typedLoader` for type-safe loading.');
  }
  const loader = typedLoader !== null && typedLoader !== void 0 ? typedLoader : untypedLoader;
  const {
    init,
    retry,
    getState,
    subscribe
  } = makeLoadable(loader, name);

  // This component only exists to re-throw any errors that occurred in the loader function,
  // and its existence allows us treat the LoadableComponent as an error boundary
  // so that we can intercept and pass along the componentStack to NR & Sentry
  const LoadableErrorThrower = ({
    errorToThrow
  }) => {
    throw errorToThrow;
  };
  class LoadableComponent extends Component {
    constructor(props) {
      super(props);
      this._mounted = false;
      this._unsubscribe = () => {};
      this._delay = 0;
      this.retry = () => {
        this.setState({
          error: null,
          didCatchError: false,
          loading: true
        }, () => {
          retry();
          this._loadModule();
        });
      };
      init();
      const {
        loading,
        result,
        error
      } = getState();
      this.state = {
        loading,
        result,
        error,
        pastDelay: typeof delay === 'number' && delay <= 0,
        spinnerRenderIsLocked: false,
        didCatchError: false,
        thrownError: null
      };
    }
    componentDidMount() {
      this._mounted = true;
      // Subscribe on every mount rather than in the constructor. React 18
      // StrictMode's simulated unmount/remount pass unsubscribes without
      // re-running the constructor, which would otherwise leave the instance
      // stuck rendering LoadingComponent forever.
      this._loadModule();
    }

    // Catch any errors that bubbled up from the loader function,
    // and send them to Sentry/NR with the componentStack
    componentDidCatch(error, errorInfo) {
      const reportedErr = new Error('Loadable: error loading split chunk', {
        cause: error
      });
      reportedErr.stack = errorInfo.componentStack;
      Metrics.counter('loadable-chunk-failed', Object.assign({}, name != null && {
        name
      })).increment();
      throwErrorAsync(reportedErr); // report errors to console/Sentry/NR #5930
    }
    componentWillUnmount() {
      this._mounted = false;
      this._clearTimers();
      callIfPossible(this._unsubscribe);
    }
    _loadModule() {
      const {
        loading
      } = getState();

      // bail if finished loading
      if (!loading) return;
      this._startTimers();
      this._unsubscribe = subscribe({
        next: res => {
          this.setState({
            loading: false,
            result: res
          });
        },
        error: err => {
          this.setState({
            loading: false,
            error: err
          });
        },
        complete: () => {
          this._clearTimers();
        }
      });
    }
    _lockSpinner() {
      if (typeof spinnerMinRenderTime === 'number' && spinnerMinRenderTime > 0) {
        this.setState({
          spinnerRenderIsLocked: true
        }, () => {
          setTimeout(() => {
            if (!this._mounted) return;
            this.setState({
              spinnerRenderIsLocked: false
            });
          }, spinnerMinRenderTime);
        });
      }
    }
    _startTimers() {
      if (typeof delay === 'number' && delay > 0) {
        this._delay = setTimeout(() => {
          this.setState({
            pastDelay: true
          }, () => {
            this._lockSpinner();
          });
        }, delay);
      }
    }
    _clearTimers() {
      clearTimeout(this._delay);
    }
    render() {
      // If the internal error boundary has already caught and reported the error,
      // return the ErrorComponent if it exists
      if (this.state.didCatchError) {
        if (ErrorComponent) {
          return /*#__PURE__*/_jsx(ErrorComponent, Object.assign({}, this.props, {
            loading: this.state.loading,
            error: this.state.thrownError,
            retry: this.retry
          }));
        }
        return null;
      } else if (this.state.error) {
        // If this.state.didCatchError is false and this.state.error is defined,
        // we're in the state where the Loadable component's loader function has thrown an error, but
        // it hasn't bubbled up to the error boundary yet.
        // In this case we'll return LoadableErrorThrower to send the error up
        // to the error boundary.
        return /*#__PURE__*/_jsx(LoadableErrorThrower, {
          errorToThrow: this.state.error
        });
      }
      if (this.state.loading || this.state.spinnerRenderIsLocked) {
        if (LoadingComponent == null) {
          return null;
        }
        return /*#__PURE__*/_jsx(LoadingComponent, Object.assign({}, this.props, {
          loading: this.state.loading,
          error: this.state.error,
          pastDelay: this.state.pastDelay,
          retry: this.retry
        }));
      } else if (this.state.result) {
        return render(this.state.result, this.props);
      } else {
        return null;
      }
    }
  }
  LoadableComponent.preload = init;
  LoadableComponent.getDerivedStateFromError = getDerivedStateFromError;
  LoadableComponent.preload = init;
  LoadableComponent.getDerivedStateFromError = getDerivedStateFromError;
  if (process.env.NODE_ENV !== 'production') {
    addPreload(LoadableComponent.preload);
  }
  return LoadableComponent;
}